import { useState, useEffect, type ReactElement } from "react";
import axios from "axios";
import { API } from "../App";
import { motion } from "framer-motion";
import { Bot, UserCheck, Timer, Mail, Loader2, type LucideIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from "recharts";

interface PerDayPoint {
  date: string;
  takeovers: number;
  ai_threads: number;
}

interface TakeoverStats {
  days: number;
  ai_threads: number;
  takeovers: number;
  step_in_rate: number;
  avg_ai_only_minutes: number;
  avg_ai_replies_before_takeover: number;
  handoffs: number;
  alerts_sent: number;
  currently_human_handled: number;
  per_day: PerDayPoint[];
}

interface StatTileProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  hint?: string;
  color: string;
  testId: string;
}

const StatTile = ({ icon: Icon, label, value, hint, color, testId }: StatTileProps) => (
  <div className="p-4 rounded-lg border border-slate-200 bg-white" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wide">
      <Icon className={`w-4 h-4 ${color}`} />
      {label}
    </div>
    <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

export const LiveChatAISection = ({ days = 30 }: { days?: number }): ReactElement => {
  const [stats, setStats] = useState<TakeoverStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    axios.get<TakeoverStats>(`${API}/webhook/hubspot/takeover-stats?days=${days}`, { withCredentials: true })
      .then((res) => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [days]);

  const chartData = (stats?.per_day || []).map((d: PerDayPoint) => ({
    ...d,
    label: new Date(d.date + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" })
  }));

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
      <Card className="border-slate-200 shadow-sm" data-testid="live-chat-ai-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Bot className="w-5 h-5 text-[#425B76]" />
            Live Chat AI
          </CardTitle>
          <p className="text-sm text-slate-500">How often agents step in and how long chats stay AI-only (last {days} days)</p>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
          {!loading && !stats && <p className="text-sm text-slate-500">Live chat stats unavailable</p>}
          {!loading && stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatTile icon={Bot} color="text-[#425B76]" label="AI chats" value={stats.ai_threads}
                  hint={`${stats.currently_human_handled} currently with an agent`} testId="stat-ai-threads" />
                <StatTile icon={UserCheck} color="text-sky-600" label="Agent step-in rate" value={`${stats.step_in_rate}%`}
                  hint={`${stats.takeovers} takeover${stats.takeovers === 1 ? "" : "s"}`} testId="stat-step-in-rate" />
                <StatTile icon={Timer} color="text-emerald-600" label="Avg AI-only time" value={`${stats.avg_ai_only_minutes} min`}
                  hint={`~${stats.avg_ai_replies_before_takeover} AI replies before an agent steps in`} testId="stat-ai-only-time" />
                <StatTile icon={Mail} color="text-amber-500" label="Handoffs" value={stats.handoffs}
                  hint={`${stats.alerts_sent} alert email${stats.alerts_sent === 1 ? "" : "s"} sent`} testId="stat-handoffs" />
              </div>
              <div className="h-[220px]" data-testid="takeover-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="ai_threads" name="AI chats" fill="#425B76" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="takeovers" name="Agent takeovers" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};
